// One-time, loss-free localStorage import + retirement (spec 027-engagement-aggregate,
// Slice D, FR-012, SC-009, User Story 4; plan §E).
//
// THE CHOSEN MIGRATION BEHAVIOUR (recorded in spec FR-012, human-confirmed): a
// ONE-TIME, idempotent, LOSS-FREE import of any existing legacy
// arcpilot.{vision,orgContext,scope} into the practitioner's FIRST/created
// engagement, then RETIRE those three keys as the persistence home. Real captured
// data is carried forward, never stranded (SC-009).
//
// This is a PURE util (no React, jsdom/SSR-safe like the sibling readers). The
// EngagementProvider CALLS it on its first-create / resolve path, handing in its
// own saveVision / saveOrganisation / saveStreams writers (the provider owns the
// engagement sub-doc PUTs + the snapshot seam). Keeping the mechanism here lets
// import + idempotency + retirement be pinned without a full provider render.
//
// Mechanism (Verifier-pinned, T-D01):
//   (1) IMPORT  — read arcpilot.vision/orgContext/scope; for each PRESENT key,
//       parse tolerantly (garbage NEVER throws) and call the matching writer with
//       the engagement sub-doc shape. vision → saveVision (verbatim), orgContext →
//       saveOrganisation (verbatim), scope { areas } → saveStreams { streams } (map
//       areas → streams, same { id, name, oneLiner?, smes } field set).
//   (2) IDEMPOTENCY — a per-engagement marker key arcpilot.imported:<engagementId>.
//       If set, no-op and return false (touch no writer, never clobber the now-
//       authoritative engagement with stale localStorage). After a successful
//       import, set it.
//   (3) RETIREMENT — after a successful import, DELETE the three legacy keys. The
//       marker and the arcpilot.engagementId pointer are NOT deleted.
//   Returns true iff at least one key was imported this call; false when nothing
//   present or already imported.

const VISION_KEY = 'arcpilot.vision';
const ORG_CONTEXT_KEY = 'arcpilot.orgContext';
const SCOPE_KEY = 'arcpilot.scope';

const markerKey = (engagementId: string): string => `arcpilot.imported:${engagementId}`;

export interface LegacyImportDeps {
  saveVision: (vision: unknown) => unknown | Promise<unknown>;
  saveOrganisation: (organisation: unknown) => unknown | Promise<unknown>;
  saveStreams: (streams: unknown) => unknown | Promise<unknown>;
}

// jsdom/SSR-safe localStorage reads/writes — never throw (storage may be absent).
function getItem(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function setItem(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // storage unavailable → no-op
  }
}

function removeItem(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // storage unavailable → no-op
  }
}

// Tolerant parse: garbage NEVER throws — a malformed key degrades to undefined so
// the import skips it (mirrors the readers' defensive parse).
function parseTolerant(raw: string | null): unknown {
  if (raw == null) return undefined;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return undefined;
  }
}

// Map the legacy scope sub-doc { areas: [...] } to the engagement streams sub-doc
// { streams: [...] } (the same { id, name, oneLiner?, smes } field set; the value
// stream is the unit). Tolerant: a non-array / absent areas yields an empty list.
function scopeToStreams(scope: unknown): { streams: unknown[] } {
  const areas =
    scope && typeof scope === 'object' && Array.isArray((scope as { areas?: unknown }).areas)
      ? ((scope as { areas: unknown[] }).areas)
      : [];
  return { streams: areas };
}

/**
 * One-time, loss-free import of any legacy localStorage sub-documents into the
 * given engagement via the provider's save writers, then retire the legacy keys.
 *
 * @returns true iff an import was performed this call (false when already imported
 *          for this engagement, or when there was nothing to import).
 */
export async function importLegacyLocalStorage(
  engagementId: string,
  deps: LegacyImportDeps,
): Promise<boolean> {
  // Idempotency guard: a second call for the same engagement does NOTHING — it
  // does not re-read / re-import, so it can never clobber the now-authoritative
  // engagement with stale localStorage.
  if (getItem(markerKey(engagementId)) != null) return false;

  const visionRaw = getItem(VISION_KEY);
  const orgRaw = getItem(ORG_CONTEXT_KEY);
  const scopeRaw = getItem(SCOPE_KEY);

  const hasVision = visionRaw != null;
  const hasOrg = orgRaw != null;
  const hasScope = scopeRaw != null;

  // Nothing present → no import, no marker, no retirement (return false).
  if (!hasVision && !hasOrg && !hasScope) return false;

  // Import only the PRESENT keys (do not fabricate missing sub-docs). Loss-free:
  // vision / organisation pass through VERBATIM (round-trippable).
  if (hasVision) {
    await deps.saveVision(parseTolerant(visionRaw));
  }
  if (hasOrg) {
    await deps.saveOrganisation(parseTolerant(orgRaw));
  }
  if (hasScope) {
    await deps.saveStreams(scopeToStreams(parseTolerant(scopeRaw)));
  }

  // Retirement: the three legacy keys are no longer the persistence home. The
  // marker + the arcpilot.engagementId pointer are kept.
  removeItem(VISION_KEY);
  removeItem(ORG_CONTEXT_KEY);
  removeItem(SCOPE_KEY);

  // Record the per-engagement idempotency marker so resolution #2+ no-ops.
  setItem(markerKey(engagementId), new Date().toISOString());

  return true;
}
